import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";

import { useUrlQuery } from "../../utils/utils";

import { getKeylessSession } from "./keylessService";
import { DomainContext } from "../../domain-repository/DomainRepository";

import { useKeyValues } from "../../components/SelectLanguage";
import Loading from "../../components/Loading";
import AlertModalManager from "../../components/alert-modal-wraper/alert-modal-manager";
import { KeylessValidateStation } from "./ValidateStation";
import { KeylessValidatePin } from "./ValidatePin";
import { KeylessValidateRA } from "./KeylessValidateRA";
import { KeylessRASignature } from "./KeylessRASignature";
import { KeylessSelfCheckout } from "./KeylessSelfCheckout";
import { KeylessVehicleReview } from "./KeylessVehicleReview";
import { KeylessTelematica } from "./KeylessTelematica";
import { KeylessValidateParkingLocation } from "./ValidateParkingLocation";

const STEPS = {
  VALIDATE_PARKING_LOCATION: "VALIDATE_PARKING_LOCATION",
  VALIDATE_STATION: "VALIDATE_STATION",
  VALIDATE_PIN: "VALIDATE_PIN",
  VALIDATE_RA: "VALIDATE_RA",
  SIGNATURE: "SIGNATURE",
  VEHICLE_REVIEW: "VEHICLE_REVIEW",
  SELF_CHECKOUT: "SELF_CHECKOUT",
  OPEN_CLOSE_DOORS: "OPEN_CLOSE_DOORS",
  DONE: "DONE",
};

export function useKeylessToken() {
  return useUrlQuery().get("token");
}

export function KeylessPage() {
  const { t } = useTranslation();

  const { isLoading: isLoadingTranslations, handleChangeLanguage } =
    useKeyValues();

  const keylessToken = useKeylessToken();

  const [currentStep, innerSetCurrentStep] = useState(null);
  const [keylessSession, setKeylessSession] = useState(null);

  const setCurrentStep = useCallback((step) => {
    innerSetCurrentStep(step);
  }, []);

  useEffect(() => {
    getKeylessSession(keylessToken)
      .then((res) => {
        setKeylessSession(res);
        innerSetCurrentStep(res.nextStep);

        handleChangeLanguage(res.language);
      })
      .catch((err) => {
        // consoante erro, se for 403 pode fazer redirect para /
        AlertModalManager.openModal(
          { title: "Error", message: t("keylessSessionError") },
          () => {}
        );
      });
  }, [keylessToken]);

  if (currentStep === null || isLoadingTranslations) {
    return <Loading active={true} />;
  }

  if (currentStep === STEPS.VALIDATE_PARKING_LOCATION) {
    return (
      <KeylessPageStyles>
        <KeylessValidateParkingLocation
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
          keylessSession={keylessSession}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.VALIDATE_STATION) {
    return (
      <KeylessPageStyles>
        <KeylessValidateStation
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
          keylessSession={keylessSession}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.VALIDATE_PIN) {
    return (
      <KeylessPageStyles>
        <KeylessValidatePin
          hasSentPin={keylessSession.hasSentPin}
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.VALIDATE_RA) {
    return (
      <KeylessPageStyles>
        <KeylessValidateRA
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.SIGNATURE) {
    return (
      <KeylessPageStyles>
        <KeylessRASignature
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
          keylessSession={keylessSession}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.VEHICLE_REVIEW) {
    return (
      <KeylessPageStyles>
        <KeylessVehicleReview
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
          keylessSession={keylessSession}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.SELF_CHECKOUT) {
    return (
      <KeylessSelfCheckout
        keylessToken={keylessToken}
        handleNextStep={setCurrentStep}
      />
    );
  }

  if (currentStep === STEPS.OPEN_CLOSE_DOORS) {
    return (
      <KeylessPageStyles>
        <KeylessTelematica
          keylessToken={keylessToken}
          handleNextStep={setCurrentStep}
          keylessSession={keylessSession}
        />
      </KeylessPageStyles>
    );
  }

  if (currentStep === STEPS.DONE) {
    return (
      <KeylessPageStyles>
        <KeylessEndPage />
      </KeylessPageStyles>
    );
  }

  return (
    <div>
      <h1>Step {currentStep} em falta</h1>
    </div>
  );
}

function KeylessView({ children, history }) {
  return (
    <main>
      <div className="container">
        <div className="row">
          <div className="col-3">
            <div onClick={() => history.push("/start")}>
              <img
                src={require(`../../assets/images/logo.png`)}
                alt="Guerin"
                className="img-fluid pt-3 pb-3"
              />
            </div>
          </div>
        </div>
        <div className="main-frame">
          <div className="container">
            <div className="row justify-content-center">{children}</div>
          </div>
        </div>
      </div>
    </main>
  );
}
export const KeylessPageLayoutView = withRouter(KeylessView);

function KeylessEndPage() {
  const { localizationAssets } = useContext(DomainContext);
  const { t } = useTranslation();

  const handleExitClick = () => {
    window.location.href = "/";
  };

  return (
    <div
      onClick={handleExitClick}
      style={{
        position: "relative",
        height: "100dvh",
        background: 'rgba(0, 0, 0, 0.4)'
      }}
    >
      <video
        src={localizationAssets["keylessEnd"]}
        autoPlay muted loop
        style={{
          height: "100dvh",
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: "-1",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          flexDirection: "column",
          paddingBottom: "60px",
          gap: "1rem",
        }}
      >
        <img
          style={{ width: "40%", margin: "7rem auto 5rem" }}
          src={localizationAssets["keylessGuerinLogo"]}
          alt="Guerin"
        />
        <h1
          style={{
            width: "85%",
            textAlign: "center",
            fontSize: "34px",
            color: "#f2f2f2bd",
          }}
        >
          {t("keylessFinalTitle")}
        </h1>
        <div style={{ flex: "1" }} />
        <div
          style={{
            width: "85%",
            textAlign: "center",
            fontSize: "24px",
            color: "#f2f2f2bd",
          }}
        >
          {t("keylessFinalMessage")}
        </div>
        <div
          style={{ textAlign: "center", fontSize: "16px", color: "#f2f2f2bd" }}
        >
          {t("keylessFinalExit")}
        </div>
      </div>
    </div>
  );
}

function KeylessPageStyles({ children }) {
  return (
    <div
      style={{
        fontFamily: "'Bliss 2'",
      }}
    >
      {children}
    </div>
  );
}
